import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { getInstructionById, deleteInstruction } from '../../api/pmsApi';

function InstructionView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);

  const [instruction, setInstruction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAllLeaders, setShowAllLeaders] = useState(false);

  useEffect(() => {
    if (!id || !token) return;
    setIsLoading(true);
    getInstructionById(id, token)
      .then(res => {
        if (res.ok) {
          setInstruction(res.data);
        }
      })
      .catch(err => {
        console.error('요청 조회 실패:', err);
        alert('데이터를 불러오는데 실패했습니다.');
        navigate('/pms/instructions');
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token, navigate]);

  if (isLoading) {
    return <div className="p-5 text-center"><div className="spinner-border text-primary" role="status"></div></div>;
  }
  if (!instruction) {
    return <div className="p-5 text-center text-muted">요청을 찾을 수 없습니다.</div>;
  }

  const statusBadgeMap = {
    pending: { label: '대기중', className: 'bg-warning text-dark' },
    under_review: { label: '검토중', className: 'bg-info text-dark' },
    rejected: { label: '반려됨', className: 'bg-danger' },
    approved: { label: '승인됨', className: 'bg-success' },
    completed: { label: '완료됨', className: 'bg-primary' },
    canceled: { label: '취소됨', className: 'bg-secondary' },
    ready: { label: '진행중', className: 'bg-primary text-white' },
    in_progress: { label: '진행중', className: 'bg-primary text-white' }
  };
  const statusInfo = statusBadgeMap[instruction.status] || { label: instruction.status, className: 'bg-secondary' };

  // 권한 확인 (작성자 및 관리자)
  const isRealAuthor = Number(user?.id) === Number(instruction.author_id);
  const isAdmin = user?.role === 'admin';
  const canEditOrDelete = isAdmin || (isRealAuthor && (instruction.status === 'pending' || instruction.status === 'under_review'));

  // 로그인한 사용자가 수신자(팀장) 목록에 포함되어 있는지 확인
  const isReceiver = instruction.leaders?.some(l => Number(l.leader_id) === Number(user?.id));

  // 로그인한 사용자 본인이 생성한 프로젝트가 있는지 확인 (삭제된 프로젝트 제외)
  const hasMyProject = instruction.projects?.some(p => Number(p.author_id) === Number(user?.id) && !p.is_deleted);

  // 삭제 처리
  const handleDelete = async () => {
    if (!window.confirm('요청을 완전히 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;
    try {
      await deleteInstruction(id, token);
      alert('요청이 삭제되었습니다.');
      navigate('/pms/instructions');
    } catch (err) {
      console.error('삭제 실패:', err);
      alert('삭제에 실패했습니다.');
    }
  };

  return (
    <div className="container-fluid p-4">
      {/* 상단 헤더 */}
      <div className="d-flex justify-content-between align-items-center mb-4 p-3 rounded shadow-sm border-0"
        style={{ background: 'linear-gradient(90deg, #607d8b 0%, #cfd8dc 100%)', color: '#fff' }}>
        <h4 className="mb-0 fw-bold">
          <i className="fas fa-eye me-2"></i>요청 조회
        </h4>
        <button className="btn btn-light fw-bold text-dark btn-sm" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left me-1"></i>뒤로가기
        </button>
      </div>

      <div className="row g-4">

        {/* 📝 좌측: 지시 내용 */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm rounded-4 mb-4">
            <div className="card-header bg-white border-bottom-0 pt-4 pb-0">
              <h5 className="fw-bold text-dark"><i className="fas fa-clipboard-list me-2 text-primary"></i>프로젝트 요청</h5>
            </div>
            <div className="card-body">

              {/* 요청 제목 */}
              <div className="mb-3">
                <label className="form-label fw-bold"><span className="text-primary fw-bold me-2 fs-5">T</span>요청 제목</label>
                <input
                  type="text"
                  className="form-control"
                  style={{ backgroundColor: '#f8f9fa', color: '#212529', fontWeight: 'bold' }}
                  value={instruction.subject || '-'}
                  readOnly
                />
              </div>

              {/* 프로젝트명 */}
              <div className="mb-3">
                <label className="form-label fw-bold"><span className="text-primary fw-bold me-2 fs-5">H</span>프로젝트</label>
                <input
                  type="text"
                  className="form-control"
                  style={{ backgroundColor: '#f8f9fa', color: '#212529' }}
                  value={instruction.title || '-'}
                  readOnly
                />
              </div>

              {/* 상세 내용 */}
              <div className="mb-2">
                <label className="form-label fw-bold"><i className="fas fa-align-left text-primary fs-5 me-2"></i>상세 요청 내용</label>
                <div
                  className="border rounded-3 bg-white p-3"
                  style={{ minHeight: '300px', fontSize: '1rem', lineHeight: '1.6' }}
                  dangerouslySetInnerHTML={{ __html: instruction.content || '<p class="text-muted">본문 내용이 없습니다.</p>' }}
                />
              </div>

              {/* 파생된 프로젝트 현황 */}
              <div className="mt-3">
                <div className="d-flex align-items-center" style={{ marginBottom: '-8px', position: 'relative', zIndex: 1 }}>
                  <i className="fas fa-network-wired text-success me-2" style={{ fontSize: '1.1rem', lineHeight: '1' }}></i>
                  <span className="fw-bold text-dark" style={{ fontSize: '1rem', letterSpacing: '-0.5px', lineHeight: '1' }}>담당자별 프로젝트 진행 현황</span>
                  {instruction.leaders && (
                    <span className="badge bg-secondary ms-2 rounded-pill shadow-sm align-self-center" style={{ fontSize: '0.65rem', padding: '0.4em 0.6em', lineHeight: '1' }}>
                      총 {instruction.leaders.length}명
                    </span>
                  )}
                </div>

                {/* 딱 붙은 테이블 영역 */}
                <div className="table-responsive border rounded-2">
                  <table className="table table-hover align-middle text-center mb-0 bg-white" style={{ fontSize: '0.85rem' }}>
                    <thead className="table-light" style={{ borderBottom: '2px solid #dee2e6' }}>
                      <tr>
                        <th className="py-1 text-secondary fw-bold" style={{ width: '25%', padding: '0.2rem', fontSize: '0.75rem' }}>책임자(PM)</th>
                        <th className="py-1 text-secondary fw-bold" style={{ width: '40%', padding: '0.2rem', fontSize: '0.75rem' }}>연결된 프로젝트명</th>
                        <th className="py-1 text-secondary fw-bold" style={{ width: '20%', padding: '0.2rem', fontSize: '0.75rem' }}>결재 상태</th>
                        <th className="py-1 text-secondary fw-bold" style={{ width: '15%', padding: '0.2rem', fontSize: '0.75rem' }}>바로가기</th>
                      </tr>
                    </thead>
                    <tbody className="border-top-0">
                      {(!instruction.leaders || instruction.leaders.length === 0) ? (
                        <tr>
                          <td colSpan="4" className="text-muted py-3">지정된 담당자가 없습니다.</td>
                        </tr>
                      ) : (
                        <>
                          {[...instruction.leaders]
                            .sort((a, b) => {
                              const nameA = a.user?.name || '';
                              const nameB = b.user?.name || '';
                              return nameA.localeCompare(nameB);
                            })
                            .slice(0, showAllLeaders ? instruction.leaders.length : 8)
                            .map(l => {
                              const fullName = `${l.user?.name || ''}${l.user?.org?.position ? ' ' + l.user.org.position : ''}${l.user?.org?.dept_name ? '(' + l.user.org.dept_name + ')' : ''}`;
                              // Find project for this leader
                              const project = instruction.projects?.find(p => p.author_id === l.leader_id && !p.is_deleted);

                              let pStatusLabel = '-';
                              let pStatusClass = 'bg-secondary';
                              if (project) {
                                if (project.status === 'ready') { pStatusLabel = '진행중'; pStatusClass = 'bg-primary text-white'; }
                                else if (project.status === 'review') { pStatusLabel = '검토중'; pStatusClass = 'bg-info text-dark'; }
                                else if (project.status === 'in_progress') { pStatusLabel = '진행중'; pStatusClass = 'bg-success'; }
                                else if (project.status === 'rejected') { pStatusLabel = '반려됨'; pStatusClass = 'bg-danger'; }
                                else if (project.status === 'completed') { pStatusLabel = '완료됨'; pStatusClass = 'bg-primary'; }
                                else if (project.status === 'canceled') { pStatusLabel = '취소됨'; pStatusClass = 'bg-dark'; }
                                else if (project.status === 'pending') { pStatusLabel = '임시저장'; pStatusClass = 'bg-secondary'; }
                                else { pStatusLabel = project.status; }
                              }

                              // 해당 프로젝트에 대한 접근 권한 로직
                              let canAccess = true;
                              if (project && (project.status === 'review' || project.status === 'rejected')) {
                                canAccess = false;
                                if (user?.role === 'admin') canAccess = true;
                                else if (Number(user?.id) === Number(project.author_id)) canAccess = true;
                                else if (Number(user?.id) === Number(project.leader_id)) canAccess = true;
                                else if (Number(user?.id) === Number(project.reviewer_id)) canAccess = true;
                              }

                              return (
                                <tr key={l.id}>
                                  <td className="text-start align-middle" style={{ padding: '0.25rem 0.25rem 0.25rem 0.75rem', fontSize: '0.75rem' }}>{fullName}</td>
                                  <td className="text-start align-middle" style={{ padding: '0.25rem', fontSize: '0.75rem' }}>
                                    {project ? (
                                      <span className="text-dark fw-medium">{project.title}</span>
                                    ) : (
                                      <span className="text-muted fst-italic">미생성 (대기중)</span>
                                    )}
                                  </td>
                                  <td className="align-middle" style={{ padding: '0.25rem' }}>
                                    {project ? (
                                      <span className={`badge ${pStatusClass} rounded-pill px-2 py-1 shadow-sm`} style={{ fontWeight: '500' }}>{pStatusLabel}</span>
                                    ) : (
                                      <span className="badge bg-light text-secondary border rounded-pill px-2 py-1" style={{ fontWeight: '500' }}>대기중</span>
                                    )}
                                  </td>
                                  <td className="align-middle" style={{ padding: '0.25rem' }}>
                                    {project && (
                                      <button 
                                        className="btn btn-sm btn-outline-primary py-0 px-2 rounded-pill shadow-sm" 
                                        style={{ fontSize: '0.7rem', height: '20px', lineHeight: '1', opacity: canAccess ? 1 : 0.4, cursor: canAccess ? 'pointer' : 'not-allowed' }} 
                                        onClick={() => { if(canAccess) navigate(`/pms/projects/${project.id}`); }}
                                        title={canAccess ? '' : '조회 권한이 없습니다.'}
                                      >
                                        이동 <i className="fas fa-external-link-alt ms-1"></i>
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}

                          {instruction.leaders.length > 8 && (
                            <tr>
                              <td colSpan="4" className="text-center p-0" style={{ borderBottom: 'none' }}>
                                <button
                                  className="btn btn-light w-100 py-2 border-0 text-primary fw-bold"
                                  onClick={() => setShowAllLeaders(!showAllLeaders)}
                                  style={{ borderTop: '1px solid #dee2e6', borderRadius: '0 0 0.375rem 0.375rem', fontSize: '0.85rem' }}
                                >
                                  {showAllLeaders ? (
                                    <><i className="fas fa-chevron-up me-1"></i> 접기</>
                                  ) : (
                                    <><i className="fas fa-chevron-down me-1"></i> {instruction.leaders.length - 8}명 더 보기</>
                                  )}
                                </button>
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ⚙️ 우측: 지시 설정 */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm rounded-4 mb-4">
            <div className="card-header bg-white border-bottom-0 pt-4 pb-0 d-flex justify-content-between align-items-center">
              <h5 className="fw-bold text-dark"><i className="fas fa-users-cog me-2 text-success"></i>요청 설정</h5>
              <span className={`badge rounded-pill px-3 py-2 ${statusInfo.className}`} style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                {statusInfo.label}
              </span>
            </div>
            <div className="card-body">

              {/* 작성자 */}
              <div className="mb-4">
                <label className="form-label fw-bold"><i className="fas fa-user-edit text-primary fs-5 me-2"></i>작성자(요청자)</label>
                <div className="fw-bold text-dark" style={{ marginLeft: '28px' }}>
                  {instruction.author?.name}
                  {instruction.author?.org?.position && (
                    <span className="ms-1 fw-normal text-secondary">
                      / {instruction.author.org.position}
                      {instruction.author.org.dept_name ? `(${instruction.author.org.dept_name})` : ''}
                    </span>
                  )}
                </div>
              </div>

              {/* 작성일시 */}
              <div className="mb-4">
                <label className="form-label fw-bold"><i className="fas fa-clock text-primary fs-5 me-2"></i>작성일시</label>
                <div className="text-secondary fw-bold" style={{ marginLeft: '28px' }}>
                  {instruction.created_at ? (() => {
                    const d = new Date(instruction.created_at);
                    if (isNaN(d.getTime())) return instruction.created_at;
                    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                  })() : '-'}
                </div>
              </div>

              {/* 프로젝트 생성 기한 */}
              <div className="mb-4">
                <label className="form-label fw-bold"><i className="fas fa-calendar-check text-primary fs-5 me-2"></i>프로젝트 생성 기한</label>
                <div className="text-danger fw-bold" style={{ marginLeft: '28px' }}>
                  {instruction.due_date ? instruction.due_date.substring(0, 10) : '-'}
                </div>
              </div>

              {/* 요청 수신자 */}
              <div className="mb-3">
                <label className="form-label fw-bold"><i className="fas fa-user text-primary fs-5 me-2"></i>책임자(PM)</label>
                <div className="p-3 bg-light rounded-3 border border-dashed" style={{ minHeight: '100px', maxHeight: '225px', overflowY: 'auto' }}>
                  {(!instruction.leaders || instruction.leaders.length === 0) ? (
                    <div className="text-center text-muted py-3">
                      <i className="fas fa-user-plus mb-2 fs-4 opacity-50"></i>
                      <p className="mb-0 small">지정된 수신자가 없습니다.</p>
                    </div>
                  ) : (
                    <div className="d-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                      {instruction.leaders.map(l => {
                        const fullName = `${l.user?.name || ''}${l.user?.org?.position ? ' / ' + l.user.org.position : ''}${l.user?.org?.dept_name ? '(' + l.user.org.dept_name + ')' : ''}`;
                        return (
                          <span key={l.id}
                            className="badge bg-white text-dark border shadow-sm px-3 py-2 d-flex align-items-center"
                            style={{ fontSize: '0.75rem', fontWeight: '500', minWidth: 0 }}
                            title={fullName}>
                            <i className="fas fa-user-tie text-primary me-2 flex-shrink-0"></i>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {fullName}
                            </span>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <hr className="text-muted opacity-25 mt-3" />

              {/* 하단 버튼 */}
              <div className="d-flex justify-content-end gap-2 pb-2">
                {/* 수신자(팀장)이면서 본인이 아직 프로젝트를 생성하지 않았을 때만 노출 (취소된 지시 제외) */}
                {isReceiver && instruction.status !== 'canceled' && !hasMyProject && (
                  <button type="button" className="btn btn-success px-2 fw-bold text-white rounded-3 shadow-sm"
                    onClick={() => {
                      const reviewerFullName = `${instruction.author?.name || ''}${instruction.author?.org?.position ? ' / ' + instruction.author.org.position : ''}${instruction.author?.org?.dept_name ? '(' + instruction.author.org.dept_name + ')' : ''}`;
                      const myLeaderObj = instruction.leaders?.find(l => Number(l.leader_id) === Number(user?.id));
                      const leaderFullName = myLeaderObj ? `${myLeaderObj.user?.name || ''}${myLeaderObj.user?.org?.position ? ' / ' + myLeaderObj.user.org.position : ''}${myLeaderObj.user?.org?.dept_name ? '(' + myLeaderObj.user.org.dept_name + ')' : ''}` : '';
                      navigate(`/pms/projects/new?instruction_id=${id}&title=${encodeURIComponent(instruction.title || '')}&reviewer_id=${instruction.author_id}&reviewer_name=${encodeURIComponent(reviewerFullName)}&leader_name=${encodeURIComponent(leaderFullName)}`);
                    }}>
                    <i className="fas fa-folder-plus me-1"></i>프로젝트 생성
                  </button>
                )}

                {/* admin은 상시 수정/삭제 가능, 작성자는 대기 또는 검토중 상태일 때만 가능 */}
                {canEditOrDelete && (
                  <>
                    <button type="button" className="btn btn-outline-primary px-2 fw-bold rounded-3"
                      onClick={() => navigate(`/pms/instructions/edit/${id}`)}>
                      <i className="fas fa-edit me-1"></i>수정
                    </button>
                    <button type="button" className="btn btn-outline-danger px-2 fw-bold rounded-3"
                      onClick={handleDelete}>
                      <i className="fas fa-trash-alt me-1"></i>삭제
                    </button>
                  </>
                )}
                <button type="button" className="btn btn-secondary px-2 fw-bold text-white rounded-3 shadow-sm" onClick={() => navigate(-1)}>
                  <i className="fas fa-list me-1"></i>목록
                </button>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default InstructionView;
